import { IonItem } from '@ionic/react';
import { Select } from 'antd';
import { FormikValues } from 'formik/dist/types';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { showError } from '../../../config/utils/formDiffUtils';
import { DocumentTypeInterface } from '../../../interfaces/documentType.interface';
import { EntityFormField } from '../../../interfaces/forms.interface';
import { useIsCloneOrEdit } from '../../../services/hooks/useIsCloneOrEdit';
import {
  getDocTypeAadeCodeToRender,
  isDocTypeDeliveryNote,
  isQuantityReceiptForm,
} from '../../../services/utils/myDataUtils';
import { ErrorForm } from '../../atoms/ErrorForm';

interface Props {
  field: EntityFormField<any>;
  formik: FormikValues;
}

export const DocType = ({ field, formik }: Props) => {
  const { t } = useTranslation();
  const setFieldValue = formik?.setFieldValue;
  const { originalCustomer } = useIsCloneOrEdit();
  const customerSelected = useMemo(() => formik?.values?.customerId, [formik?.values?.customerId]);
  const customerAddress = useMemo(() => customerSelected?.address, [customerSelected?.address]);
  const hasError = useMemo(() => showError(formik, formik?.errors?.[field.name]), [field.name, formik]);
  const documentType = useMemo(
    () => formik?.values?.documentTypeId?.aadeCode || formik?.values?.documentTypeId?.object?.aadeCode,
    [formik?.values?.documentTypeId?.aadeCode, formik?.values?.documentTypeId?.object?.aadeCode],
  );
  const isMarkedAsDeliveryNote = useMemo(() => formik?.values?.isDeliveryNote, [formik?.values?.isDeliveryNote]);
  const isDeliveryFieldsRequired = useMemo(
    () => (isDocTypeDeliveryNote(documentType) || isMarkedAsDeliveryNote) && !isQuantityReceiptForm(documentType),
    [documentType, isMarkedAsDeliveryNote],
  );

  /**
   * When delivery note set `deliverTo` address with customer address
   */
  useEffect(() => {
    if (isDeliveryFieldsRequired) {
      if (customerAddress && customerSelected?.id !== originalCustomer && customerAddress !== field.value) {
        setFieldValue('shipTo', customerAddress?.slice(0, 198));
      }
    }
  }, [
    documentType,
    customerSelected,
    isMarkedAsDeliveryNote,
    customerAddress,
    originalCustomer,
    field.value,
    isDeliveryFieldsRequired,
    setFieldValue,
  ]);

  const onChange = (value: string, option: any) => {
    formik.setFieldValue(field.name, option);
  };

  const getOptions = useCallback(() => {
    // @ts-expect-error mismatch of interfaces
    return field?.data?.map((option: DocumentTypeInterface) => {
      const docTypeAadeCode = getDocTypeAadeCodeToRender(option?.aadeCode);
      return {
        label: option ? `(${docTypeAadeCode}) ` + t(`DOC_TYPES.${option?.name}`) : '',
        value: option.id,
        object: option,
      };
    });
  }, [field?.data, t]);

  return (
    <div>
      <div className="padding-b-16 padding-l-12">{t('SALE.DOC_TYPE')} *</div>
      <div
        key={field.name}
        className="flex-center-start-column-nowrap positionRelative min-width-300px max-width-460px">
        <IonItem
          className={`min-width-300px max-width-460px border-radius-input g-select-border ${
            hasError && 'b-field-border-error'
          }`}
          mode="md">
          <Select
            id="invoiceDocTypeSelect"
            placeholder={t(`SALE.${field?.placeholder}`) + '...'}
            className="my-ant-select normalText5"
            variant="borderless"
            onChange={onChange}
            options={getOptions()}
            defaultValue={formik.values[field.name]}
          />
        </IonItem>
        <ErrorForm styleName="margin-t-minus-14">
          {formik.errors?.[field.name] && formik.errors?.[field.name]}
        </ErrorForm>
      </div>
    </div>
  );
};
